import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { Bookmark } from '../../types/api';
import { ThumbnailPlaceholderIcon } from '../Bookmark/Row/BookmarkThumbnail';

interface SelectionStackProps {
  bookmarks: Bookmark[];
  showThumbnails: boolean;
  badgeCount?: number;
  allowPlaceholderFallback?: boolean;
  /** Rendered in place of the stack when every frame's image fails to load and
   *  no placeholder fallback is allowed — otherwise the stack would show nothing
   *  but its background glow. */
  fallback?: ReactNode;
}

/* eslint-disable lingui/no-unlocalized-strings -- CSS rotation values, not user-facing */
const FRAME_STYLES: Array<{ rotation: string; zIndex: number }> = [
  { rotation: '-6deg', zIndex: 1 },
  { rotation: '0deg', zIndex: 2 },
  { rotation: '6deg', zIndex: 3 },
];
/* eslint-enable lingui/no-unlocalized-strings */

function SelectionStack({
  bookmarks,
  showThumbnails,
  badgeCount,
  allowPlaceholderFallback = true,
  fallback = null,
}: SelectionStackProps) {
  const [failedIds, setFailedIds] = useState<Set<string>>(() => new Set());

  const frameCount = bookmarks.length;

  if (frameCount === 0) {
    return null;
  }

  const handleImageError = (id: string) =>
    setFailedIds((prev) => {
      if (prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  // Only frames that will actually render something (an image or a placeholder).
  // Frames whose image failed with no placeholder fallback are dropped, so we can
  // re-derive tilts from their remaining position and keep the stack symmetric.
  const visibleBookmarks = bookmarks.filter((bookmark) => {
    const showImage =
      showThumbnails && Boolean(bookmark.og_image) && !failedIds.has(bookmark.id);
    return showImage || allowPlaceholderFallback;
  });

  // Every frame dropped its image and there is no placeholder to fall back on,
  // so the stack would render only its glow. Show the fallback instead.
  if (visibleBookmarks.length === 0) {
    return <>{fallback}</>;
  }

  const visibleCount = visibleBookmarks.length;

  return (
    <div className="selection-stack__imageStack" data-count={visibleCount}>
      <div className="selection-stack__glow" />
      {visibleBookmarks.map((bookmark, index) => {
        // Tilt is assigned by position among the visible frames (not the original
        // index), so dropping a failed frame re-centers the survivors.
        const style =
          visibleCount === 1
            ? { rotation: '0deg' as const, zIndex: 1 }
            : FRAME_STYLES[index];
        return (
          <FrameContent
            key={bookmark.id}
            bookmark={bookmark}
            showThumbnails={showThumbnails}
            allowPlaceholderFallback={allowPlaceholderFallback}
            hasImageError={failedIds.has(bookmark.id)}
            onImageError={() => handleImageError(bookmark.id)}
            rotation={style.rotation}
            zIndex={style.zIndex}
          />
        );
      })}
      {typeof badgeCount === 'number' && badgeCount > 0 && (
        <div className="selection-stack__badge" aria-hidden>
          {badgeCount}
        </div>
      )}
    </div>
  );
}

interface FrameContentProps {
  bookmark: Bookmark;
  showThumbnails: boolean;
  allowPlaceholderFallback: boolean;
  hasImageError: boolean;
  onImageError: () => void;
  rotation: string;
  zIndex: number;
}

function FrameContent({
  bookmark,
  showThumbnails,
  allowPlaceholderFallback,
  hasImageError,
  onImageError,
  rotation,
  zIndex,
}: FrameContentProps) {
  const showImage = showThumbnails && bookmark.og_image && !hasImageError;
  const showPlaceholderFallback = allowPlaceholderFallback && !showImage;

  if (!showImage && !showPlaceholderFallback) {
    return null;
  }

  return (
    <div
      className="selection-stack__frame"
      style={
        {
          '--rotation': rotation,
          '--z-index': zIndex,
        } as CSSProperties
      }
    >
      {showImage ? (
        <img
          src={bookmark.og_image}
          alt=""
          className="selection-stack__image"
          onError={onImageError}
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center"
          style={{ backgroundColor: 'var(--bg-placeholder)' }}
        >
          <ThumbnailPlaceholderIcon />
        </div>
      )}
    </div>
  );
}

export default SelectionStack;
